import { useState, useCallback } from 'react';

/**
 * 画像クロッピング機能を提供するカスタムフック
 * @param {number} defaultAspectRatio デフォルトのアスペクト比
 * @returns {Object} クロッパー関連の状態と関数
 */
export const useCropper = (defaultAspectRatio = 1) => {
  // クロッパーの状態管理
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [showCropper, setShowCropper] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(defaultAspectRatio);
  const [croppedImage, setCroppedImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * クロップ完了時の処理
   */
  const onCropComplete = useCallback(async (croppedArea, croppedAreaPixels) => {
    if (!originalImage || isProcessing) return;

    try {
      setIsProcessing(true);
      
      // Canvasを使用して画像をクロップ
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // 元画像を読み込み
      const image = new Image();
      image.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = originalImage;
      });

      // Canvas設定
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      // クロップ処理
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      // Base64形式でデータURLを生成
      const croppedImageUrl = canvas.toDataURL('image/png', 0.9);
      setCroppedImage(croppedImageUrl);

    } catch (error) {
      console.error('クロップ処理中にエラーが発生しました:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [originalImage, isProcessing]);

  /**
   * クロッパーを開く
   * @param {string} imageUrl 画像のURL
   * @param {number} aspectRatio アスペクト比（オプション）
   */
  const openCropper = useCallback((imageUrl, aspectRatio) => {
    setOriginalImage(imageUrl);
    if (aspectRatio !== undefined) {
      setAspectRatio(aspectRatio);
    }
    setShowCropper(true);
  }, []);

  /**
   * クロッパーを閉じる
   */
  const closeCropper = useCallback(() => {
    setShowCropper(false);
    setCroppedImage(null);
    setOriginalImage(null);
    setIsProcessing(false);
  }, []);

  /**
   * クロッパーの状態をリセット
   */
  const resetCropper = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedImage(null);
    setIsProcessing(false);
  }, []);

  /**
   * ズームをリセット
   */
  const resetZoom = useCallback(() => {
    setZoom(1);
  }, []);

  return {
    // 状態
    crop,
    zoom,
    showCropper,
    aspectRatio,
    croppedImage,
    originalImage,
    isProcessing,
    
    // セッター
    setCrop,
    setZoom,
    setAspectRatio,
    
    // メソッド
    onCropComplete,
    openCropper,
    closeCropper,
    resetCropper,
    resetZoom,
  };
};
